import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Typography, Stack, Grid, Chip, Button, Tooltip, IconButton, Dialog, DialogTitle, DialogContent } from '@mui/material';
import { keyframes } from '@mui/system';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import HowToRegRoundedIcon from '@mui/icons-material/HowToRegRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import SavingsRoundedIcon from '@mui/icons-material/SavingsRounded';
import MoreTimeRoundedIcon from '@mui/icons-material/MoreTimeRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import BeachAccessRoundedIcon from '@mui/icons-material/BeachAccessRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import WorkRoundedIcon from '@mui/icons-material/WorkRounded';
import PersonSearchRoundedIcon from '@mui/icons-material/PersonSearchRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import BuildCircleRoundedIcon from '@mui/icons-material/BuildCircleRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setActiveEntity } from '../redux/slices/orgSlice';
import http, { apiErrorMessage } from '../Api/http';
import { GetMasterDashboard } from '../Api/Api';
import Loader from '../components/Loader';
import { LoadAlert } from '../components/uiKit';

const PRIMARY = '#7C5CFC';
const PRIMARY_LIGHT = '#F1EEFE';
const PRIMARY_BORDER = '#C9BEFB';
const INK = '#0F172A';
const card = { bgcolor: '#fff', border: '1px solid #E6EAF1', borderRadius: '12px', boxShadow: '0 1px 3px rgba(16,24,40,0.06)' };

const fadeUp = keyframes`from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; }`;
const enter = (i) => ({ animation: `${fadeUp} .5s cubic-bezier(.22,1,.36,1) both`, animationDelay: `${i * 0.05}s` });

const n = (v) => Number(v) || 0;
const num = (v) => n(v).toLocaleString('en-IN');
const compactInr = (v) => {
    const x = n(v); const a = Math.abs(x);
    if (a >= 1e7) return `₹${(x / 1e7).toFixed(a >= 1e8 ? 0 : 1)}Cr`;
    if (a >= 1e5) return `₹${(x / 1e5).toFixed(a >= 1e6 ? 0 : 1)}L`;
    if (a >= 1e3) return `₹${(x / 1e3).toFixed(a >= 1e4 ? 0 : 1)}K`;
    return `₹${Math.round(x)}`;
};
const initials = (s = '') => s.split(' ').filter(Boolean).map((w) => w[0]).join('').slice(0, 2).toUpperCase();

const fmtNiceDate = (dmy) => {
    const m = String(dmy || '').match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (!m) return dmy || '';
    const d = new Date(`${m[3]}-${m[2]}-${m[1]}`);
    return Number.isNaN(d.getTime()) ? dmy : d.toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' });
};

const EMP_COLOR = { Permanent: '#7C5CFC', Probation: '#F59E0B', Contract: '#0EA5E9', Internship: '#16A34A', 'Part-Time': '#E11D48' };
const empColor = (k) => EMP_COLOR[k] || '#94A3B8';

const normEntity = (e) => ({
    id: e.entityId,
    name: e.entityName ?? '',
    code: e.shortCode ?? '',
    city: e.city ?? '',
    color: e.entityColour || PRIMARY,
    active: e.isActive !== false,
    setupReady: e.setup?.ready === true,
    pendingRequired: n(e.setup?.pendingRequired),
    employees: n(e.workforce?.totalEmployees),
    present: n(e.attendanceToday?.present),
    presentPercent: n(e.attendanceToday?.presentPercent),
    marked: n(e.attendanceToday?.marked),
    pendingApprovals: n(e.pendingApprovals),
    payrollBadge: e.payroll?.statusBadge || (e.payroll?.hasCycle ? 'In progress' : 'No cycle'),
    payrollHasCycle: e.payroll?.hasCycle === true,
    docsMissing: (Array.isArray(e.documents?.missingByType) ? e.documents.missingByType : []).reduce((s, g) => s + n(g.count), 0),
});

// One white card: icon + title (+ small subtitle) on the left, optional link on the right.
function Panel({ icon: Icon, title, hint, action, onAction, iconColor = PRIMARY, iconBg, children, sx, i = 0 }) {
    return (
        <Box sx={{ ...card, borderRadius: '10px', p: 2, height: '100%', display: 'flex', flexDirection: 'column', ...enter(i), ...sx }}>
            {(title || action) && (
                <Stack direction="row" sx={{ alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1.6 }}>
                    <Stack direction="row" spacing={1.2} sx={{ alignItems: 'center', minWidth: 0 }}>
                        {Icon && (
                            <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: iconBg || PRIMARY_LIGHT, border: `1px solid ${iconBg ? `${iconColor}26` : PRIMARY_BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <Icon sx={{ fontSize: 19, color: iconColor }} />
                            </Box>
                        )}
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: 15, fontWeight: 800, color: INK, lineHeight: 1.2 }}>{title}</Typography>
                            {hint && <Typography sx={{ fontSize: 11.5, color: '#94A3B8', mt: 0.2 }}>{hint}</Typography>}
                        </Box>
                    </Stack>
                    {action && (
                        <Button onClick={onAction} endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 15 }} />} sx={{ textTransform: 'none', fontSize: 12.5, fontWeight: 700, color: PRIMARY, borderRadius: '7px', px: 1.2, flexShrink: 0, '&:hover': { bgcolor: PRIMARY_LIGHT } }}>{action}</Button>
                    )}
                </Stack>
            )}
            {children}
        </Box>
    );
}

// Purple "View … →" link pinned to the bottom of a card.
function FootLink({ label, onClick }) {
    return (
        <Box sx={{ mt: 'auto', pt: 1.6 }}>
            <Button onClick={onClick} endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 15 }} />} sx={{ p: 0, minWidth: 0, textTransform: 'none', fontSize: 13, fontWeight: 700, color: PRIMARY, '&:hover': { bgcolor: 'transparent', opacity: 0.8 } }}>{label}</Button>
        </Box>
    );
}

export default function MasterDashboardPage() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const userName = useSelector((s) => s.auth.userName);
    const organisation = useSelector((s) => s.auth.organisation);

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [docKey, setDocKey] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data: body } = await http.get(GetMasterDashboard);
            if (body?.error) throw new Error(body.message || 'Could not load the master dashboard.');
            setData(body?.data || null);
            setLoadError('');
        } catch (err) {
            setLoadError(apiErrorMessage(err, 'Could not load the master dashboard.'));
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const t = data?.totals || {};
    const wf = t.workforce || {};
    const att = t.attendanceToday || {};
    const pay = t.payroll || {};
    const rec = t.recruitment || {};

    const entities = useMemo(
        () => (Array.isArray(data?.entities) ? data.entities : [])
            .map(normEntity)
            .sort((a, b) => (a.active === b.active ? b.employees - a.employees : (a.active ? -1 : 1))),
        [data],
    );

    const empTypes = useMemo(() => Object.entries(wf.byEmploymentType || {}).map(([k, v]) => ({ k, v: n(v) })), [wf.byEmploymentType]);
    const empTotal = empTypes.reduce((s, x) => s + x.v, 0) || 1;

    const openEntity = (e) => { if (!e.active) return; dispatch(setActiveEntity(e.id)); navigate('/dashboard'); };
    const missingDocGroups = Array.isArray(t.documents?.missingByType) ? t.documents.missingByType : [];
    const openMissingDocEmployee = (emp) => {
        setDocKey(null);
        if (emp.entityId != null) dispatch(setActiveEntity(emp.entityId));
        navigate(`/dashboard/employees/${emp.id}`);
    };
    const activeDocGroup = missingDocGroups.find((g) => g.documentType === docKey) || null;
    const docEmployees = Array.isArray(activeDocGroup?.employees) ? activeDocGroup.employees : [];

    if (loading) return <Loader />;
    if (loadError) {
        return (
            <Box sx={{ p: 2 }}>
                <LoadAlert sx={{ borderRadius: '9px' }} action={<Button size="small" onClick={load} sx={{ textTransform: 'none', fontWeight: 700 }}>Retry</Button>}>{loadError}</LoadAlert>
            </Box>
        );
    }

    const heroStats = [
        { label: 'Entities', value: num(t.entities), sub: `${n(t.activeEntities)} active · ${n(t.inactiveEntities)} inactive`, icon: ApartmentRoundedIcon, color: PRIMARY, bg: '#EDE9FE' },
        { label: 'Workforce', value: num(wf.totalEmployees), sub: `${n(wf.active)} active`, icon: GroupsRoundedIcon, color: PRIMARY, bg: '#EDE9FE' },
        { label: 'Present today', value: num(att.present), sub: `${n(att.presentPercent)}% · ${n(att.marked)} marked`, icon: HowToRegRoundedIcon, color: '#15803D', bg: '#DCFCE7' },
        { label: 'Monthly payout', value: compactInr(pay.grossPayout), sub: pay.hasCycle ? (pay.statusBadge || 'in progress') : 'no cycle', icon: PaymentsRoundedIcon, color: PRIMARY, bg: '#EDE9FE' },
    ];

    const attention = [
        { key: 'documents', label: 'Document approvals', value: n(t.documents?.pendingApprovals), icon: DescriptionRoundedIcon, color: '#0EA5E9', to: '/dashboard/documents?tab=approvals' },
        { key: 'leave', label: 'Leave requests', value: n(t.leave?.pendingRequests), icon: BeachAccessRoundedIcon, color: '#0891B2', to: '/dashboard/leave-management' },
        { key: 'overtime', label: 'Overtime requests', value: n(t.overtime?.pendingRequests), icon: MoreTimeRoundedIcon, color: '#F59E0B', to: '/dashboard/pay-adjustments?tab=overtime' },
        { key: 'advances', label: 'Advance requests', value: n(t.advances?.pendingRequests), icon: SavingsRoundedIcon, color: '#16A34A', to: '/dashboard/pay-adjustments?tab=advances' },
        { key: 'setup', label: 'Entities need setup', value: n(t.entitiesNeedingSetup), icon: BuildCircleRoundedIcon, color: '#E11D48', to: '/dashboard/entities', sub: (v) => `${v} ${v === 1 ? 'entity requires' : 'entities require'} setup` },
        { key: 'probation', label: 'Probation ending', value: n(wf.probationEndingSoon), icon: HowToRegRoundedIcon, color: '#7C5CFC', to: '/dashboard/employees' },
        { key: 'resignations', label: 'Resignations pending', value: n(t.resignations?.pending), icon: LogoutRoundedIcon, color: '#DC2626', to: '/dashboard/employees' },
    ].filter((x) => x.value > 0);
    const attentionTotal = attention.reduce((s, x) => s + n(x.value), 0);

    const attRows = [
        { label: 'Present', value: n(att.present), color: '#16A34A' },
        { label: 'Absent', value: n(att.absent), color: '#F59E0B' },
        { label: 'On leave', value: n(att.onLeave), color: '#0891B2' },
        { label: 'Late', value: n(att.late), color: '#E11D48' },
        { label: 'Not marked', value: n(att.notMarked), color: '#94A3B8' },
    ];

    const pipeline = [
        { label: 'Open vacancies', value: n(rec.openVacancies), icon: WorkRoundedIcon, color: '#7C5CFC', to: '/dashboard/recruitment?tab=vacancies' },
        { label: 'Candidates in pipeline', value: n(rec.candidatesInPipeline), icon: PersonSearchRoundedIcon, color: '#0EA5E9', to: '/dashboard/recruitment?tab=candidates' },
        { label: 'Interviews today', value: n(rec.interviewsToday), icon: EventAvailableRoundedIcon, color: '#16A34A', to: '/dashboard/recruitment?tab=interviews' },
        { label: 'OT hours this month', value: n(t.overtime?.approvedHoursThisMonth), icon: MoreTimeRoundedIcon, color: '#F59E0B', to: '/dashboard/pay-adjustments?tab=overtime' },
        { label: 'Advances outstanding', value: compactInr(t.advances?.outstandingAmount), icon: SavingsRoundedIcon, color: '#0891B2', to: '/dashboard/pay-adjustments?tab=advances', money: true },
    ];

    const maxHeadcount = Math.max(1, ...entities.map((e) => e.employees));
    const maxPending = Math.max(1, ...entities.map((e) => e.pendingApprovals));
    const orgName = organisation || entities[0]?.name || '';

    return (
        <Box sx={{ p: 2 }}>
            {/* Header */}
            <Box sx={{ ...enter(0), p: { xs: 2, md: 2.6 }, borderRadius: '14px', bgcolor: '#F5F3FF', border: '1px solid #E4DEFB' }}>
                <Stack direction="row" sx={{ alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.7px', color: PRIMARY }}>MASTER CONTROL</Typography>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        <Tooltip arrow title="Reload">
                            <IconButton onClick={load} size="small" sx={{ color: '#64748B', '&:hover': { bgcolor: PRIMARY_LIGHT, color: PRIMARY } }}>
                                <RefreshRoundedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                        <Button onClick={() => navigate('/dashboard/entities')} endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 17 }} />} sx={{ height: 40, px: 2, textTransform: 'none', fontWeight: 700, borderRadius: '8px', color: '#fff', bgcolor: PRIMARY, boxShadow: `0 2px 6px ${PRIMARY}40`, '&:hover': { bgcolor: '#6246E0' } }}>Manage Entities</Button>
                    </Stack>
                </Stack>
                <Typography sx={{ fontSize: { xs: 22, md: 26 }, fontWeight: 800, color: INK, letterSpacing: '-0.3px', lineHeight: 1.15, mt: 1.4 }}>Group Command Center</Typography>
                <Typography sx={{ fontSize: 13.5, color: '#64748B', mt: 0.8 }}>
                    Manage and monitor all your business entities in one place.{userName ? ` · ${userName}` : ''}
                </Typography>
                <Box sx={{ borderTop: '1px solid #E4DEFB', mt: 2, pt: 1.4 }}>
                    <Stack direction="row" spacing={1.4} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5, color: '#64748B' }}>
                        {orgName && (
                            <Stack direction="row" spacing={0.6} sx={{ alignItems: 'center' }}>
                                <ApartmentRoundedIcon sx={{ fontSize: 15, color: PRIMARY }} />
                                <Typography sx={{ fontSize: 12.5 }}>{orgName}</Typography>
                            </Stack>
                        )}
                        <Stack direction="row" spacing={0.6} sx={{ alignItems: 'center' }}>
                            <CalendarMonthRoundedIcon sx={{ fontSize: 15, color: PRIMARY }} />
                            <Typography sx={{ fontSize: 12.5 }}>{fmtNiceDate(data?.generatedOn)}</Typography>
                        </Stack>
                    </Stack>
                </Box>
            </Box>

            {/* Stats — four equal tiles in one row */}
            <Grid container spacing={1.25} sx={{ mt: 2 }}>
                {heroStats.map((s, i) => (
                    <Grid size={{ xs: 6, md: 3 }} key={s.label}>
                        <Box sx={{ ...enter(i + 1), height: '100%', p: 2, borderRadius: '10px', bgcolor: s.bg, border: `1px solid ${s.color}26` }}>
                            <s.icon sx={{ fontSize: 18, color: s.color }} />
                            <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.4px', color: s.color, textTransform: 'uppercase', mt: 0.8 }}>{s.label}</Typography>
                            <Typography sx={{ fontSize: 26, fontWeight: 800, color: INK, lineHeight: 1.15, mt: 0.4 }}>{s.value}</Typography>
                            <Typography sx={{ fontSize: 11.5, color: '#64748B', mt: 0.3 }}>{s.sub}</Typography>
                        </Box>
                    </Grid>
                ))}
            </Grid>

            {/* Attendance + Payroll */}
            <Grid container spacing={1.5} sx={{ mt: 1.5 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Panel icon={HowToRegRoundedIcon} title="Attendance · Today" i={3}>
                        <Typography sx={{ fontSize: 32, fontWeight: 800, color: PRIMARY, lineHeight: 1 }}>{n(att.presentPercent)}%</Typography>
                        <Typography sx={{ fontSize: 12.5, color: '#64748B', mt: 0.6 }}>Present · {n(att.marked)} of {n(wf.active)} marked</Typography>
                        <Box sx={{ borderTop: '1px solid #EEF0F6', mt: 1.6, pt: 1.2 }}>
                            <Stack spacing={1.1}>
                                {attRows.map((r) => (
                                    <Stack key={r.label} direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: r.color }} />
                                            <Typography sx={{ fontSize: 12.5, color: '#475569' }}>{r.label}</Typography>
                                        </Stack>
                                        <Typography sx={{ fontSize: 13, fontWeight: 800, color: INK }}>{num(r.value)}</Typography>
                                    </Stack>
                                ))}
                            </Stack>
                        </Box>
                        <FootLink label="View Attendance" onClick={() => navigate('/dashboard/attendance-leave?tab=overview')} />
                    </Panel>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <Panel icon={PaymentsRoundedIcon} title="Payroll" i={4}>
                        <Typography sx={{ fontSize: 12.5, color: '#64748B' }}>{pay.latestMonth || 'Current period'}</Typography>
                        <Chip label={pay.statusBadge || (pay.hasCycle ? 'In progress' : 'No cycle')} size="small" sx={{ alignSelf: 'flex-start', height: 22, fontSize: 11, fontWeight: 800, mt: 0.8, mb: 1.6, bgcolor: pay.hasCycle ? '#DCFCE7' : '#F1F5F9', color: pay.hasCycle ? '#16A34A' : '#64748B' }} />
                        <Stack direction="row" spacing={1.2} sx={{ mb: 1.4 }}>
                            {[['Gross', compactInr(pay.grossPayout)], ['Net', compactInr(pay.netPayout)]].map(([l, v]) => (
                                <Box key={l} sx={{ flex: 1, p: 1.4, borderRadius: '10px', bgcolor: '#F8FAFC', border: '1px solid #EEF0F6' }}>
                                    <Typography sx={{ fontSize: 10, color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>{l}</Typography>
                                    <Typography sx={{ fontSize: 20, fontWeight: 800, color: INK, mt: 0.3 }}>{v}</Typography>
                                </Box>
                            ))}
                        </Stack>
                        <Typography sx={{ fontSize: 12, color: '#64748B' }}>
                            {num(pay.employeesInCycle)} employees · {num(pay.cyclesInProgress)} cycle{n(pay.cyclesInProgress) === 1 ? '' : 's'} running
                        </Typography>
                        <FootLink label="Run Payroll" onClick={() => navigate('/dashboard/run-payroll?tab=register')} />
                    </Panel>
                </Grid>
            </Grid>

            {/* Workforce + Waiting on you */}
            <Grid container spacing={1.5} sx={{ mt: 1.5 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Panel icon={GroupsRoundedIcon} title="Workforce" hint={`New this month: ${n(wf.newJoinersThisMonth)}`} i={6}>
                        <Typography sx={{ fontSize: 32, fontWeight: 800, color: INK, lineHeight: 1 }}>{num(wf.totalEmployees)}</Typography>
                        <Typography sx={{ fontSize: 12.5, color: '#64748B', mt: 0.6 }}>{n(wf.active)} active employees</Typography>
                        <Box sx={{ borderTop: '1px solid #EEF0F6', mt: 1.6, pt: 1.4 }}>
                            <Stack spacing={1.2}>
                                <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
                                    <Typography sx={{ fontSize: 12.5, color: '#475569' }}>Inactive</Typography>
                                    <Typography sx={{ fontSize: 13, fontWeight: 800, color: INK }}>{num(wf.inactive)}</Typography>
                                </Stack>
                                {empTypes.map((et) => (
                                    <Box key={et.k}>
                                        <Stack direction="row" sx={{ justifyContent: 'space-between', mb: 0.4 }}>
                                            <Typography sx={{ fontSize: 12.5, color: '#475569' }}>{et.k}</Typography>
                                            <Typography sx={{ fontSize: 13, fontWeight: 800, color: INK }}>{et.v}</Typography>
                                        </Stack>
                                        <Box sx={{ height: 6, borderRadius: '4px', bgcolor: '#F1F5F9', overflow: 'hidden' }}>
                                            <Box sx={{ height: '100%', width: `${(et.v / empTotal) * 100}%`, bgcolor: empColor(et.k), borderRadius: '4px' }} />
                                        </Box>
                                    </Box>
                                ))}
                                {empTypes.length === 0 && <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>No employees yet.</Typography>}
                            </Stack>
                        </Box>
                        <FootLink label="Employees" onClick={() => navigate('/dashboard/employees')} />
                    </Panel>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <Panel icon={ErrorOutlineRoundedIcon} iconColor="#E11D48" iconBg="#FEE2E2" title="Waiting on You" hint={`${num(attentionTotal)} action${attentionTotal === 1 ? '' : 's'} · ${num(t.pendingApprovals)} approvals`} i={7}>
                        {attention.length === 0 ? (
                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'center', py: 3 }}>
                                <CheckCircleRoundedIcon sx={{ fontSize: 20, color: '#16A34A' }} />
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#16A34A' }}>All clear across the group — nothing pending right now.</Typography>
                            </Stack>
                        ) : (
                            <Box sx={{ borderTop: '1px solid #EEF0F6' }}>
                                {attention.map((c, idx) => (
                                    <Stack
                                        key={c.key} direction="row" spacing={1.4} onClick={() => navigate(c.to)}
                                        sx={{ alignItems: 'center', py: 1.3, cursor: 'pointer', borderTop: idx === 0 ? 'none' : '1px solid #F4F5F9', '&:hover': { bgcolor: '#FAFAFD' }, '&:hover .att-arrow': { opacity: 1 } }}
                                    >
                                        <c.icon sx={{ fontSize: 19, color: c.color, flexShrink: 0 }} />
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: INK }} noWrap>{c.label}</Typography>
                                            <Typography sx={{ fontSize: 11.5, color: '#94A3B8' }} noWrap>{c.sub ? c.sub(c.value) : `${num(c.value)} pending`}</Typography>
                                        </Box>
                                        <Typography sx={{ fontSize: 15, fontWeight: 800, color: INK }}>{num(c.value)}</Typography>
                                        <ArrowForwardRoundedIcon className="att-arrow" sx={{ fontSize: 16, color: PRIMARY, opacity: 0.55, transition: 'opacity .15s' }} />
                                    </Stack>
                                ))}
                            </Box>
                        )}
                        {attention.length > 0 && <FootLink label={attention[0].key === 'setup' ? 'Review setup' : 'Review all'} onClick={() => navigate(attention[0].to)} />}
                    </Panel>
                </Grid>
            </Grid>

            {/* Left: missing documents, headcount and pending approvals stacked; right: business entities */}
            <Grid container spacing={1.5} sx={{ mt: 1.5 }}>
                <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
                <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
                    <Panel sx={{ height: 'auto' }} icon={DescriptionRoundedIcon} title="Missing Documents" hint="Employees missing required KYC documents across every entity" action="View all" onAction={() => navigate('/dashboard/employees')} i={5}>
                        {missingDocGroups.length === 0 ? (
                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'center', py: 2 }}>
                                <CheckCircleRoundedIcon sx={{ fontSize: 18, color: '#16A34A' }} />
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#16A34A' }}>No missing KYC documents across the group.</Typography>
                            </Stack>
                        ) : (
                            <Grid container spacing={1.5}>
                                {missingDocGroups.map((g) => (
                                    <Grid size={{ xs: 12, sm: 6 }} key={g.documentType}>
                                        <Box
                                            onClick={() => setDocKey(g.documentType)}
                                            sx={{ p: 1.8, height: '100%', borderRadius: '10px', border: '1px solid #E6EAF1', cursor: 'pointer', transition: 'background-color .15s', '&:hover': { bgcolor: '#FAFAFD' }, '&:hover .doc-arrow': { opacity: 1 } }}
                                        >
                                            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                                                <DescriptionRoundedIcon sx={{ fontSize: 17, color: PRIMARY }} />
                                                <ArrowForwardRoundedIcon className="doc-arrow" sx={{ fontSize: 17, color: PRIMARY, opacity: 0, transition: 'opacity .15s' }} />
                                            </Stack>
                                            <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: INK, mt: 0.8 }}>{g.label}</Typography>
                                            <Typography sx={{ fontSize: 26, fontWeight: 800, color: INK, lineHeight: 1.2, mt: 0.4 }}>{num(g.count)}</Typography>
                                            <Typography sx={{ fontSize: 11.5, color: '#94A3B8' }}>missing</Typography>
                                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: PRIMARY, mt: 1 }}>View details</Typography>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </Panel>
                    <Panel sx={{ height: 'auto' }} icon={GroupsRoundedIcon} title="Headcount by Entity" hint="Employees per company" i={9}>
                        {entities.some((e) => e.employees > 0) ? (
                            <Stack spacing={1.2}>
                                {entities.map((e) => (
                                    <Box key={e.id} sx={{ position: 'relative', overflow: 'hidden', borderRadius: '10px', bgcolor: '#F5F3FF', border: '1px solid #E4DEFB' }}>
                                        <Stack direction="row" spacing={1.4} sx={{ position: 'relative', alignItems: 'center', p: 1.3 }}>
                                            <Box data-keep-colour sx={{ width: 26, alignSelf: 'stretch', minHeight: 34, borderRadius: '4px', bgcolor: e.color, opacity: 0.35 + 0.65 * (e.employees / maxHeadcount), flexShrink: 0 }} />
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: INK }} noWrap>{e.code || e.name}</Typography>
                                                <Typography sx={{ fontSize: 11.5, color: '#64748B' }}>{num(e.employees)} employee{e.employees === 1 ? '' : 's'}</Typography>
                                            </Box>
                                        </Stack>
                                    </Box>
                                ))}
                            </Stack>
                        ) : (
                            <Typography sx={{ fontSize: 12.5, color: '#94A3B8', textAlign: 'center', py: 5 }}>No employees on record yet.</Typography>
                        )}
                    </Panel>
                    {/* The last panel takes the leftover height so the left column ends level with Business Entities. */}
                    <Panel sx={{ height: 'auto', flexGrow: 1 }} icon={FactCheckRoundedIcon} title="Pending Approvals by Entity" hint="Where work is piling up" i={10}>
                        {entities.some((e) => e.pendingApprovals > 0) ? (
                            <Stack spacing={1.2}>
                                {entities.filter((e) => e.pendingApprovals > 0).map((e) => (
                                    <Box key={e.id} sx={{ position: 'relative', overflow: 'hidden', borderRadius: '10px', bgcolor: '#FFF7ED', border: '1px solid #FCEBD0' }}>
                                        <Stack direction="row" spacing={1.4} sx={{ position: 'relative', alignItems: 'center', p: 1.3 }}>
                                            <Box sx={{ width: 26, alignSelf: 'stretch', minHeight: 34, borderRadius: '4px', bgcolor: '#F59E0B', opacity: 0.35 + 0.65 * (e.pendingApprovals / maxPending), flexShrink: 0 }} />
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: INK }} noWrap>{e.code || e.name}</Typography>
                                                <Typography sx={{ fontSize: 11.5, color: '#64748B' }}>{num(e.pendingApprovals)} pending</Typography>
                                            </Box>
                                        </Stack>
                                    </Box>
                                ))}
                            </Stack>
                        ) : (
                            <Stack sx={{ alignItems: 'center', justifyContent: 'center', py: 4, flexGrow: 1, borderRadius: '10px', bgcolor: '#F8FAFC' }} spacing={0.8}>
                                <CheckCircleRoundedIcon sx={{ fontSize: 20, color: '#16A34A' }} />
                                <Typography sx={{ fontSize: 12.5, color: '#64748B' }}>Nothing pending across the group</Typography>
                            </Stack>
                        )}
                    </Panel>
                </Stack>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                <Panel icon={ApartmentRoundedIcon} title="Business Entities" hint={`${num(t.entities)} ${n(t.entities) === 1 ? 'company' : 'companies'} · ${n(t.activeEntities)} active · ${n(t.entitiesNeedingSetup)} need setup`} action="Manage" onAction={() => navigate('/dashboard/entities')} i={8}>
                    {entities.length === 0 ? (
                        <Typography sx={{ fontSize: 13, color: '#94A3B8', textAlign: 'center', py: 2 }}>No entities yet.</Typography>
                    ) : (
                        <Grid container spacing={2}>
                            {entities.map((e) => (
                                <Grid size={12} key={e.id}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, px: 2, py: 1.6, borderRadius: '10px', border: '1px solid #E6EAF1', opacity: e.active ? 1 : 0.72 }}>
                                        {/* identity */}
                                        <Stack direction="row" spacing={1.4} sx={{ alignItems: 'center', flex: '1 1 190px', minWidth: 0 }}>
                                            <Box data-keep-colour sx={{ width: 44, height: 44, borderRadius: '11px', bgcolor: `${e.color}1A`, color: e.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 14.5, flexShrink: 0 }}>
                                                {initials(e.name) || <ApartmentRoundedIcon />}
                                            </Box>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography sx={{ fontSize: 14.5, fontWeight: 800, color: INK }} noWrap>{e.name || 'Unnamed'}</Typography>
                                                <Typography sx={{ fontSize: 11.5, color: '#94A3B8' }} noWrap>{e.code}{e.city ? ` · ${e.city}` : ''}</Typography>
                                                <Stack direction="row" spacing={0.6} sx={{ mt: 0.5, alignItems: 'center' }}>
                                                    <Chip label={e.active ? 'Active' : 'Inactive'} size="small" sx={{ height: 18, fontSize: 9.5, fontWeight: 800, bgcolor: e.active ? '#DCFCE7' : '#F1F5F9', color: e.active ? '#16A34A' : '#64748B' }} />
                                                    <Chip label={e.payrollBadge} size="small" sx={{ height: 18, fontSize: 9.5, fontWeight: 800, bgcolor: e.payrollHasCycle ? '#EDE9FE' : '#F1F5F9', color: e.payrollHasCycle ? PRIMARY : '#94A3B8' }} />
                                                </Stack>
                                            </Box>
                                        </Stack>

                                        {/* numbers */}
                                        <Stack direction="row" spacing={1} sx={{ flex: '1 1 190px' }}>
                                            {[['Staff', num(e.employees)], ['Present', num(e.present)], ['Pending', num(e.pendingApprovals)]].map(([l, v]) => (
                                                <Box key={l} sx={{ flex: 1, textAlign: 'center', py: 1.1, borderRadius: '9px', bgcolor: '#F8FAFC', border: '1px solid #EEF0F6' }}>
                                                    <Typography sx={{ fontSize: 17, fontWeight: 800, color: INK, lineHeight: 1 }}>{v}</Typography>
                                                    <Typography sx={{ fontSize: 10, color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, mt: 0.5 }}>{l}</Typography>
                                                </Box>
                                            ))}
                                        </Stack>

                                        {/* status + action */}
                                        <Stack spacing={0.9} sx={{ flex: '0 1 170px', minWidth: 150 }}>
                                            <Stack direction="row" spacing={0.7} sx={{ alignItems: 'center', minWidth: 0 }}>
                                                {e.setupReady ? (
                                                    <><CheckCircleRoundedIcon sx={{ fontSize: 16, color: '#16A34A' }} /><Typography sx={{ fontSize: 12, color: '#16A34A', fontWeight: 700 }} noWrap>Setup complete</Typography></>
                                                ) : (
                                                    <><ErrorOutlineRoundedIcon sx={{ fontSize: 16, color: '#E11D48' }} /><Typography sx={{ fontSize: 12, color: '#E11D48', fontWeight: 600 }} noWrap>{e.pendingRequired} setup step{e.pendingRequired === 1 ? '' : 's'} pending</Typography></>
                                                )}
                                            </Stack>
                                            {e.docsMissing > 0 && (
                                                <Stack direction="row" spacing={0.7} sx={{ alignItems: 'center', minWidth: 0 }}>
                                                    <DescriptionRoundedIcon sx={{ fontSize: 15, color: '#F59E0B' }} />
                                                    <Typography sx={{ fontSize: 12, color: '#B45309', fontWeight: 600 }} noWrap>{e.docsMissing} missing KYC doc{e.docsMissing === 1 ? '' : 's'}</Typography>
                                                </Stack>
                                            )}
                                            <Button
                                                fullWidth onClick={() => openEntity(e)} disabled={!e.active}
                                                endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 17 }} />}
                                                sx={{ height: 36, textTransform: 'none', fontWeight: 700, fontSize: 12.5, borderRadius: '9px', color: '#fff', bgcolor: PRIMARY, boxShadow: 'none', '&:hover': { bgcolor: '#6246E0' }, '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' } }}
                                            >
                                                {e.active ? 'Enter Workspace' : 'Inactive'}
                                            </Button>
                                        </Stack>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </Panel>
                </Grid>
            </Grid>

            {/* Recruitment & finance */}
            <Box sx={{ mt: 1.5 }}>
                <Panel icon={WorkRoundedIcon} title="Recruitment & Finance" hint="Across every entity" i={11}>
                    <Grid container spacing={2}>
                        {pipeline.map((p) => (
                            <Grid size={{ xs: 6, sm: 4, md: 12 / 5 }} key={p.label}>
                                <Box onClick={() => navigate(p.to)} sx={{ p: 1.8, height: '100%', borderRadius: '10px', border: '1px solid #E6EAF1', cursor: 'pointer', transition: 'background-color .15s', '&:hover': { bgcolor: '#FAFAFD' } }}>
                                    <p.icon sx={{ fontSize: 19, color: p.color }} />
                                    <Typography sx={{ fontSize: 22, fontWeight: 800, color: INK, lineHeight: 1.1, mt: 0.8 }}>{p.money ? p.value : num(p.value)}</Typography>
                                    <Typography sx={{ fontSize: 11.5, color: '#64748B', fontWeight: 600, mt: 0.3 }} noWrap>{p.label}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Panel>
            </Box>

            <Dialog open={Boolean(activeDocGroup)} onClose={() => setDocKey(null)} fullWidth maxWidth="xs" slotProps={{ paper: { sx: { borderRadius: '12px' } } }}>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.2, pr: 1.5, py: 1.6 }}>
                    <Box sx={{ width: 34, height: 34, borderRadius: '9px', bgcolor: PRIMARY_LIGHT, border: `1px solid ${PRIMARY_BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <DescriptionRoundedIcon sx={{ fontSize: 18, color: PRIMARY }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 15, fontWeight: 800, color: INK }} noWrap>{activeDocGroup?.label}</Typography>
                        <Typography sx={{ fontSize: 11.5, color: '#94A3B8' }}>{activeDocGroup?.count} missing across the group</Typography>
                    </Box>
                    <IconButton size="small" onClick={() => setDocKey(null)} aria-label="Close"><CloseRoundedIcon sx={{ fontSize: 18 }} /></IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0, maxHeight: 420 }}>
                    {docEmployees.length === 0 ? (
                        <Typography sx={{ fontSize: 12.5, color: '#98A0AE', p: 2 }}>No employee details available.</Typography>
                    ) : docEmployees.map((e, idx) => (
                        <Stack
                            key={`${e.entityId ?? ''}-${e.id ?? idx}`} direction="row"
                            onClick={() => e.id != null && openMissingDocEmployee(e)}
                            sx={{ alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.1, cursor: e.id != null ? 'pointer' : 'default', '&:hover': e.id != null ? { bgcolor: '#F8FAFC' } : undefined, borderTop: idx === 0 ? 'none' : '1px solid #F7F7FB' }}
                        >
                            <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: '#334155' }} noWrap>{e.name}</Typography>
                                <Typography sx={{ fontSize: 11, color: '#98A0AE' }} noWrap>{e.employeeCode}{e.entityName ? ` · ${e.entityName}` : ''}</Typography>
                            </Box>
                            {e.id != null && <ArrowForwardRoundedIcon sx={{ fontSize: 14, color: '#C4CBD6', flexShrink: 0 }} />}
                        </Stack>
                    ))}
                </DialogContent>
            </Dialog>
        </Box>
    );
}
